import React, { useState } from "react";
import {
  IResourceComponentsProps,
  useShow,
  useTranslate,
} from "@refinedev/core";
import { DateField } from "@refinedev/antd";
import { useNavigate } from "react-router-dom";
import { Spin, Row, Col, Space, Typography, Tag } from "antd";
import {
  PageContainer,
  PageHeader,
  HeroCard,
  GlassCard,
  ActionButton,
} from "../../shared/components";
import { usePageStyles } from "../../shared/styles";
import { ProductEditModal } from "./ProductEditModal";

const { Text } = Typography;

export const ProductShow: React.FC<IResourceComponentsProps> = () => {
  const _t = useTranslate();
  const navigate = useNavigate();
  const { queryResult } = useShow();
  const { data, isLoading } = queryResult;
  const styles = usePageStyles();
  const [editModalOpen, setEditModalOpen] = useState(false);

  const record = data?.data;

  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "400px",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
        <PageHeader
          breadcrumbs={[
            {
              label: _t("products.products"),
              onClick: () => navigate("/products"),
            },
            { label: record?.name || "" },
          ]}
          actions={
            <>
              <ActionButton
                variant="secondary"
                onClick={() => navigate("/products")}
              >
                {_t("products.titles.list")}
              </ActionButton>
              <ActionButton
                variant="primary"
                onClick={() => setEditModalOpen(true)}
              >
                {_t("buttons.edit")}
              </ActionButton>
            </>
          }
        />

        <HeroCard
          title={record?.name || ""}
          subtitle={<Text type="secondary">{record?.code}</Text>}
          meta={
            <>
              <div style={{ textAlign: "right" as const }}>
                <div style={styles.metaLabel}>{_t("fields.createdAt")}</div>
                <div style={styles.metaValue}>
                  <DateField value={record?.createdAt} format="DD/MM/YYYY" />
                </div>
              </div>
              <div style={{ textAlign: "right" as const }}>
                <div style={styles.metaLabel}>{_t("fields.updatedAt")}</div>
                <div style={styles.metaValue}>
                  <DateField value={record?.updatedAt} format="DD/MM/YYYY" />
                </div>
              </div>
            </>
          }
        />

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: "24px",
          }}
        >
          <GlassCard title={_t("products.fields.basicInfo")}>
            <Row gutter={[24, 24]}>
              <Col xs={24} sm={12} md={8}>
                <Space direction="vertical" size="small">
                  <Text type="secondary">{_t("products.fields.price")}</Text>
                  <Text
                    strong
                    style={{ fontSize: "1.2em", color: styles.primaryColor }}
                  >
                    {record?.price ? `${record.price}` : "-"}
                  </Text>
                </Space>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Space direction="vertical" size="small">
                  <Text type="secondary">{_t("products.fields.group")}</Text>
                  <Text strong>{record?.group ?? "-"}</Text>
                </Space>
              </Col>
              <Col xs={24} sm={24}>
                <Space direction="vertical" size="small">
                  <Text type="secondary">{_t("products.fields.tags")}</Text>
                  {record?.tags?.length ? (
                    <Space size={[4, 4]} wrap>
                      {record.tags.map((tag: string) => (
                        <Tag key={tag}>{tag}</Tag>
                      ))}
                    </Space>
                  ) : (
                    <Text strong>-</Text>
                  )}
                </Space>
              </Col>
            </Row>
          </GlassCard>

          <GlassCard title={_t("products.fields.description")}>
            <Text>{record?.description ?? "-"}</Text>
          </GlassCard>
        </div>

        <ProductEditModal
          open={editModalOpen}
          productId={record?.id ?? null}
          onCancel={() => setEditModalOpen(false)}
          onSuccess={() => {
            setEditModalOpen(false);
            queryResult?.refetch();
          }}
        />

    </div>
  );
};
